import React, { useEffect, useMemo, useState } from "react";
import { ApiService } from "../services/api";
import {
  AiListeSonuc,
  AraSonuc,
  AiYorumSonuc,
  Band,
  FiltreSecenekleri,
  PuanTuru,
  TercihFiltreler,
  TercihProgram,
} from "../types/tercih";

const PUAN_TURLERI: PuanTuru[] = ["TYT", "SAY", "SÖZ", "EA", "DİL"];

const BAND_META: Record<Band, { label: string; renk: string; nokta: string; badge: string }> = {
  guvenli: {
    label: "Güvenli",
    renk: "border-emerald-200 bg-emerald-50",
    nokta: "bg-emerald-500",
    badge: "bg-emerald-100 text-emerald-700",
  },
  orta: {
    label: "Orta",
    renk: "border-amber-200 bg-amber-50",
    nokta: "bg-amber-500",
    badge: "bg-amber-100 text-amber-700",
  },
  riskli: {
    label: "Riskli",
    renk: "border-rose-200 bg-rose-50",
    nokta: "bg-rose-500",
    badge: "bg-rose-100 text-rose-700",
  },
};

const ProgramCard: React.FC<{ p: TercihProgram }> = ({ p }) => (
  <div className="flex flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3 sm:flex-row sm:items-center sm:justify-between">
    <div className="min-w-0">
      <div className="truncate font-medium text-gray-900">{p.ad}</div>
      <div className="truncate text-sm text-gray-500">
        {p.universite} · {p.fakulte}
      </div>
      <div className="mt-1 flex flex-wrap gap-1">
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{p.sehir}</span>
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">{p.tur}</span>
        {p.burs && p.burs !== "Devlet" && (
          <span className="rounded-full bg-blue-50 px-2 py-0.5 text-xs text-blue-600">{p.burs}</span>
        )}
        <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-600">
          {p.duzey === "lisans" ? "Lisans" : "Ön lisans"}
        </span>
      </div>
    </div>
    <div className="shrink-0 text-right">
      <div className="text-sm font-semibold text-gray-900">
        {p.basariSirasi.toLocaleString("tr-TR")}. sıra
      </div>
      <div className="text-xs text-gray-500">
        Taban puan {Number(p.enKucukPuan).toFixed(1)}
      </div>
    </div>
  </div>
);

const TercihRobotu: React.FC = () => {
  const [puanTuru, setPuanTuru] = useState<PuanTuru | "">("");
  const [basariSirasi, setBasariSirasi] = useState<string>("");
  const [filtreler, setFiltreler] = useState<TercihFiltreler>({});
  const [secenekler, setSecenekler] = useState<FiltreSecenekleri | null>(null);
  const [sonuc, setSonuc] = useState<AraSonuc | null>(null);
  const [aiYorum, setAiYorum] = useState<AiYorumSonuc | null>(null);
  const [yukleniyor, setYukleniyor] = useState(false);
  const [aiYukleniyor, setAiYukleniyor] = useState(false);
  const [hata, setHata] = useState<string>("");
  const [aktifBand, setAktifBand] = useState<Band>("guvenli");

  // Bölüm çoklu-seçim
  const [bolumArama, setBolumArama] = useState("");

  // AI kişisel liste + iletişim
  const [lead, setLead] = useState({ adSoyad: "", email: "", telefon: "" });
  const [aiListe, setAiListe] = useState<AiListeSonuc | null>(null);
  const [aiListeYukleniyor, setAiListeYukleniyor] = useState(false);

  useEffect(() => {
    ApiService.getTercihFiltreler(puanTuru || undefined)
      .then(setSecenekler)
      .catch(() => setSecenekler(null));
  }, [puanTuru]);

  const araButonAktif = puanTuru && basariSirasi && Number(basariSirasi) > 0;

  const handleAra = async () => {
    if (!araButonAktif) return;
    setYukleniyor(true);
    setHata("");
    setAiYorum(null);
    setAiListe(null);
    try {
      const res = await ApiService.tercihAra({
        puanTuru: puanTuru as string,
        basariSirasi: Number(basariSirasi),
        filtreler,
      });
      setSonuc(res);
      const ilk: Band = res.sayilar.guvenli ? "guvenli" : res.sayilar.orta ? "orta" : "riskli";
      setAktifBand(ilk);
    } catch (e: any) {
      setHata("Arama başarısız oldu. Lütfen tekrar deneyin.");
    } finally {
      setYukleniyor(false);
    }
  };

  const handleAiYorum = async () => {
    if (!puanTuru || !basariSirasi) return;
    setAiYukleniyor(true);
    try {
      const res = await ApiService.tercihAiYorum({
        puanTuru: puanTuru as string,
        basariSirasi: Number(basariSirasi),
        filtreler,
      });
      setAiYorum(res);
    } catch {
      setAiYorum({ yorum: null, hata: "AI yorumu üretilemedi." });
    } finally {
      setAiYukleniyor(false);
    }
  };

  const handleAiListe = async () => {
    if (!lead.adSoyad || (!lead.email && !lead.telefon)) return;
    setAiListeYukleniyor(true);
    try {
      const res = await ApiService.tercihAiListe({
        puanTuru: puanTuru as string,
        basariSirasi: Number(basariSirasi),
        filtreler,
        adSoyad: lead.adSoyad,
        email: lead.email || undefined,
        telefon: lead.telefon || undefined,
      });
      setAiListe(res);
    } catch {
      setAiListe({ strateji: null, liste: [], hata: "Liste oluşturulamadı, tekrar deneyin." });
    } finally {
      setAiListeYukleniyor(false);
    }
  };

  const toggleBolum = (b: string) => {
    setFiltreler((f) => {
      const cur = new Set(f.bolumler || []);
      cur.has(b) ? cur.delete(b) : cur.add(b);
      return { ...f, bolumler: Array.from(cur) };
    });
  };

  const toggleSehir = (s: string) => {
    setFiltreler((f) => {
      const cur = new Set(f.sehirler || []);
      cur.has(s) ? cur.delete(s) : cur.add(s);
      return { ...f, sehirler: Array.from(cur) };
    });
  };

  const toggleBurs = (b: string) => {
    setFiltreler((f) => {
      const cur = new Set(f.burslar || []);
      cur.has(b) ? cur.delete(b) : cur.add(b);
      return { ...f, burslar: Array.from(cur) };
    });
  };

  const bandListesi = sonuc ? sonuc.gruplar[aktifBand] : [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Üst şerit */}
      <header className="border-b border-gray-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center gap-2 px-4 py-4">
          <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-blue-600 text-white font-bold">
            Y
          </div>
          <div>
            <div className="font-semibold text-gray-900">YKS Tercih Robotu</div>
            <div className="text-xs text-gray-500">2026 · Başarı sıranla kazanabileceğin bölümler</div>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-6">
        {/* Giriş kartı */}
        <section className="rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
          <h2 className="mb-4 text-lg font-semibold text-gray-900">Bilgilerini gir</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Puan türü</label>
              <div className="flex flex-wrap gap-2">
                {PUAN_TURLERI.map((pt) => (
                  <button
                    key={pt}
                    onClick={() => setPuanTuru(pt)}
                    className={`rounded-lg border px-4 py-2 text-sm font-medium transition ${
                      puanTuru === pt
                        ? "border-blue-600 bg-blue-600 text-white"
                        : "border-gray-300 bg-white text-gray-700 hover:border-blue-400"
                    }`}
                  >
                    {pt}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Başarı sırası</label>
              <input
                type="text"
                inputMode="numeric"
                value={basariSirasi ? Number(basariSirasi).toLocaleString("tr-TR") : ""}
                onChange={(e) => setBasariSirasi(e.target.value.replace(/[^\d]/g, ""))}
                placeholder="Örn. 45.000"
                className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none focus:ring-1 focus:ring-blue-600"
              />
              <p className="mt-1 text-xs text-gray-500">YKS sonuç belgendeki sıralaman (boşluk/nokta fark etmez).</p>
            </div>
          </div>

          {/* Filtreler */}
          <div className="mt-5 space-y-4 border-t border-gray-100 pt-5">
            <div className="grid gap-4 sm:grid-cols-3">
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Düzey</label>
                <select
                  value={filtreler.duzey || ""}
                  onChange={(e) =>
                    setFiltreler((f) => ({ ...f, duzey: (e.target.value || undefined) as any }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
                >
                  <option value="">Hepsi</option>
                  <option value="lisans">Lisans (4 yıl)</option>
                  <option value="onlisans">Ön lisans (2 yıl)</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">Üniversite türü</label>
                <select
                  value={filtreler.tur || ""}
                  onChange={(e) =>
                    setFiltreler((f) => ({ ...f, tur: (e.target.value || undefined) as any }))
                  }
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
                >
                  <option value="">Devlet + Vakıf</option>
                  <option value="Devlet">Sadece Devlet</option>
                  <option value="Vakıf">Sadece Vakıf</option>
                </select>
              </div>
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Bölüm seç{" "}
                  {(filtreler.bolumler || []).length > 0 && `(${filtreler.bolumler!.length})`}
                </label>
                <input
                  value={bolumArama}
                  onChange={(e) => setBolumArama(e.target.value)}
                  placeholder="Bölüm ara ve seç (örn. Bilgisayar)"
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
                />
              </div>
            </div>

            {/* Bölüm çoklu-seçim: seçili chip'ler + arama sonuçları */}
            {secenekler && (
              <div>
                {(filtreler.bolumler || []).length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-1.5">
                    {(filtreler.bolumler || []).map((b) => (
                      <button
                        key={b}
                        onClick={() => toggleBolum(b)}
                        className="rounded-full border border-blue-600 bg-blue-600 px-2.5 py-0.5 text-xs text-white"
                      >
                        {b} ✕
                      </button>
                    ))}
                  </div>
                )}
                {bolumArama.trim().length >= 2 && (
                  <div className="flex max-h-40 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 p-2">
                    {secenekler.bolumler
                      .filter(
                        (b) =>
                          b.ad.toLocaleLowerCase("tr-TR").includes(bolumArama.toLocaleLowerCase("tr-TR")) &&
                          !(filtreler.bolumler || []).includes(b.ad)
                      )
                      .slice(0, 40)
                      .map((b) => (
                        <button
                          key={b.ad}
                          onClick={() => {
                            toggleBolum(b.ad);
                            setBolumArama("");
                          }}
                          className="rounded-full border border-gray-300 bg-white px-2.5 py-0.5 text-xs text-gray-700 hover:border-blue-400"
                        >
                          {b.ad} <span className="text-gray-400">({b.sayi})</span>
                        </button>
                      ))}
                    {secenekler.bolumler.filter(
                      (b) =>
                        b.ad.toLocaleLowerCase("tr-TR").includes(bolumArama.toLocaleLowerCase("tr-TR")) &&
                        !(filtreler.bolumler || []).includes(b.ad)
                    ).length === 0 && (
                      <span className="px-1 text-xs text-gray-400">Eşleşen bölüm yok</span>
                    )}
                  </div>
                )}
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-gray-700">Burs durumu</label>
              <div className="flex flex-wrap gap-2">
                {["Burslu", "İndirimli", "Ücretli"].map((b) => (
                  <button
                    key={b}
                    onClick={() => toggleBurs(b)}
                    className={`rounded-full border px-3 py-1 text-xs font-medium transition ${
                      (filtreler.burslar || []).includes(b)
                        ? "border-blue-600 bg-blue-50 text-blue-700"
                        : "border-gray-300 bg-white text-gray-600 hover:border-blue-400"
                    }`}
                  >
                    {b}
                  </button>
                ))}
              </div>
            </div>

            {secenekler && (
              <div>
                <label className="mb-1 block text-sm font-medium text-gray-700">
                  Şehir {(filtreler.sehirler || []).length > 0 && `(${filtreler.sehirler!.length} seçili)`}
                </label>
                <div className="flex max-h-28 flex-wrap gap-1.5 overflow-y-auto rounded-lg border border-gray-100 bg-gray-50 p-2">
                  {secenekler.sehirler.map((s) => (
                    <button
                      key={s}
                      onClick={() => toggleSehir(s)}
                      className={`rounded-full border px-2.5 py-0.5 text-xs transition ${
                        (filtreler.sehirler || []).includes(s)
                          ? "border-blue-600 bg-blue-600 text-white"
                          : "border-gray-300 bg-white text-gray-600 hover:border-blue-400"
                      }`}
                    >
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          <button
            onClick={handleAra}
            disabled={!araButonAktif || yukleniyor}
            className="mt-5 w-full rounded-lg bg-blue-600 py-2.5 font-medium text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300 sm:w-auto sm:px-8"
          >
            {yukleniyor ? "Aranıyor…" : "Programları Bul"}
          </button>
          {hata && <p className="mt-2 text-sm text-rose-600">{hata}</p>}
        </section>

        {/* Sonuç */}
        {sonuc && (
          <section className="mt-6">
            {/* Özet */}
            <div className="grid grid-cols-3 gap-3">
              {(["guvenli", "orta", "riskli"] as Band[]).map((b) => (
                <button
                  key={b}
                  onClick={() => setAktifBand(b)}
                  className={`rounded-xl border p-4 text-left transition ${
                    aktifBand === b ? BAND_META[b].renk : "border-gray-200 bg-white hover:border-gray-300"
                  }`}
                >
                  <div className="flex items-center gap-2">
                    <span className={`h-2.5 w-2.5 rounded-full ${BAND_META[b].nokta}`} />
                    <span className="text-sm font-medium text-gray-700">{BAND_META[b].label}</span>
                  </div>
                  <div className="mt-1 text-2xl font-bold text-gray-900">{sonuc.sayilar[b]}</div>
                  <div className="text-xs text-gray-500">program</div>
                </button>
              ))}
            </div>

            {/* AI yorum */}
            <div className="mt-4 rounded-xl border border-blue-100 bg-blue-50/50 p-4">
              {!aiYorum && (
                <button
                  onClick={handleAiYorum}
                  disabled={aiYukleniyor}
                  className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:bg-gray-300"
                >
                  {aiYukleniyor ? "Yorum hazırlanıyor…" : "🤖 AI'dan strateji yorumu al"}
                </button>
              )}
              {aiYorum?.yorum && (
                <div>
                  <div className="mb-1 text-sm font-semibold text-blue-800">AI Tercih Danışmanı</div>
                  <p className="whitespace-pre-line text-sm leading-relaxed text-gray-700">{aiYorum.yorum}</p>
                </div>
              )}
              {aiYorum?.hata && <p className="text-sm text-rose-600">{aiYorum.hata}</p>}
            </div>

            {/* Band listesi */}
            <div className="mt-4">
              <div className="mb-2 flex items-center gap-2">
                <span className={`h-3 w-3 rounded-full ${BAND_META[aktifBand].nokta}`} />
                <h3 className="font-semibold text-gray-900">
                  {BAND_META[aktifBand].label} programlar ({bandListesi.length})
                </h3>
              </div>
              {bandListesi.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-300 bg-white p-6 text-center text-sm text-gray-500">
                  Bu bantta program yok. Filtreleri gevşetmeyi dene (şehri genişlet, vakıfı ekle).
                </p>
              ) : (
                <div className="space-y-2">
                  {bandListesi.map((p) => (
                    <ProgramCard key={p.kod} p={p} />
                  ))}
                </div>
              )}
            </div>

            {/* AI kişisel tercih listesi */}
            <div className="mt-6 rounded-xl border border-blue-200 bg-white p-5">
              <div className="text-lg font-semibold text-gray-900">🎯 Sana özel AI tercih listesi</div>
              <p className="mb-4 mt-1 text-sm text-gray-500">
                Bilgilerini bırak; seçtiğin bölümler ve puanına göre AI, güvenliden iddialıya dengeli,
                sıralı bir tercih listesi hazırlasın.
              </p>
              <div className="grid gap-3 sm:grid-cols-3">
                <input
                  value={lead.adSoyad}
                  onChange={(e) => setLead({ ...lead, adSoyad: e.target.value })}
                  placeholder="Ad Soyad"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
                />
                <input
                  value={lead.email}
                  onChange={(e) => setLead({ ...lead, email: e.target.value })}
                  placeholder="E-posta"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
                />
                <input
                  value={lead.telefon}
                  onChange={(e) => setLead({ ...lead, telefon: e.target.value })}
                  placeholder="Telefon"
                  className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-600 focus:outline-none"
                />
              </div>
              <button
                onClick={handleAiListe}
                disabled={!lead.adSoyad || (!lead.email && !lead.telefon) || aiListeYukleniyor}
                className="mt-3 rounded-lg bg-blue-600 px-6 py-2.5 text-sm font-medium text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-gray-300"
              >
                {aiListeYukleniyor ? "Listen hazırlanıyor…" : "✨ AI tercih listemi oluştur"}
              </button>

              {aiListe && (
                <div className="mt-5">
                  {aiListe.hata && <p className="text-sm text-rose-600">{aiListe.hata}</p>}
                  {aiListe.strateji && (
                    <div className="mb-3 rounded-lg bg-blue-50 p-3 text-sm leading-relaxed text-gray-700">
                      <span className="font-semibold text-blue-800">Strateji: </span>
                      {aiListe.strateji}
                    </div>
                  )}
                  {aiListe.liste.length > 0 && (
                    <>
                      <div className="mb-2 text-sm font-semibold text-gray-900">
                        Önerilen tercih listen ({aiListe.liste.length})
                      </div>
                      <div className="space-y-2">
                        {aiListe.liste.map((p) => (
                          <div key={p.kod} className="flex gap-3 rounded-lg border border-gray-200 p-3">
                            <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-blue-600 text-xs font-bold text-white">
                              {p.sira}
                            </div>
                            <div className="min-w-0 flex-1">
                              <div className="flex items-center gap-2">
                                <span className={`h-2 w-2 shrink-0 rounded-full ${BAND_META[p.band].nokta}`} />
                                <span className="truncate font-medium text-gray-900">{p.ad}</span>
                              </div>
                              <div className="truncate text-sm text-gray-500">
                                {p.universite} · {p.sehir} · {p.tur}
                                {p.burs && p.burs !== "Devlet" ? ` · ${p.burs}` : ""}
                              </div>
                              {p.gerekce && <div className="mt-1 text-xs text-gray-600">{p.gerekce}</div>}
                            </div>
                            <div className="shrink-0 text-right text-xs text-gray-500">
                              {p.basariSirasi.toLocaleString("tr-TR")}. sıra
                            </div>
                          </div>
                        ))}
                      </div>
                      <p className="mt-3 text-xs font-medium text-emerald-700">
                        ✓ Bilgilerin kaydedildi. Koç desteği için sana ulaşabiliriz.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            <p className="mt-4 text-center text-xs text-gray-400">
              Veriler 2025-YKS taban sıralarına dayanır (ÖSYM 2026 kontenjan kılavuzu). 2026 sonuçları
              değişebilir; bu araç yol göstericidir, garanti değildir.
            </p>
          </section>
        )}
      </main>
    </div>
  );
};

export default TercihRobotu;
